import React, { useState, useEffect } from 'react';
import Header from '../../component/Header';
import Sidebar from '../../component/SideBar';
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';
import Apiconnection from "../../utils/Apiconnection";
import Loader from "../../utils/Loader";
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Predefined 3 policy types as required
const PREDEFINED_POLICIES = [
    {
        type: 'refund_policy',
        title: 'Refund Policy',
        badgeClass: 'policy-badge-refund',
        defaultDescription: '<h1>Refund Policy</h1><p>All sales are final...</p>',
    },
    {
        type: 'privacy_policy',
        title: 'Privacy Policy',
        badgeClass: 'policy-badge-privacy',
        defaultDescription: '<h1>Privacy Policy</h1><p>Your privacy content here...</p>',
    },
    {
        type: 'terms_and_conditions',
        title: 'Terms and Conditions',
        badgeClass: 'policy-badge-terms',
        defaultDescription: '<h1>Terms and Conditions</h1><p>Your terms and conditions content here...</p>',
    },
];

const Policies = () => {
    const [openSidebarToggle, setOpenSidebarToggle] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [activeTab, setActiveTab] = useState('listing'); // 'listing' | 'legacy'

    // Content pages state
    const [contentPages, setContentPages] = useState([]);

    // Create / Edit modal state
    const [openEditModal, setOpenEditModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
    const [formData, setFormData] = useState({
        id: null,
        type: 'refund_policy',
        title: 'Refund Policy',
        description: '',
    });

    // View modal state
    const [openViewModal, setOpenViewModal] = useState(false);
    const [viewingPolicy, setViewingPolicy] = useState(null);

    // Delete modal state
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [policyToDelete, setPolicyToDelete] = useState(null);

    // Backward-compatibility: Legacy General Policy state (get-policies / policies-add-update)
    const [legacyPolicy, setLegacyPolicy] = useState('');
    const [legacyEditorHtml, setLegacyEditorHtml] = useState('');
    const [isLegacyEditorOpen, setIsLegacyEditorOpen] = useState(false);

    const OpenSidebar = () => {
        setOpenSidebarToggle(!openSidebarToggle);
    };

    const getAuthToken = () => {
        return localStorage.getItem('prime_access_token') || '';
    };

    // Helper to safely extract list from API response
    const extractListFromResponse = (response) => {
        if (!response?.data) return [];
        const { data } = response;
        if (Array.isArray(data?.data)) return data.data;
        if (Array.isArray(data?.data?.list)) return data.data.list;
        if (Array.isArray(data?.data?.rows)) return data.data.rows;
        if (Array.isArray(data?.data?.contentPages)) return data.data.contentPages;
        if (Array.isArray(data?.data?.pages)) return data.data.pages;
        if (Array.isArray(data?.data?.content)) return data.data.content;
        if (Array.isArray(data?.data?.result)) return data.data.result;
        if (Array.isArray(data?.list)) return data.list;
        if (Array.isArray(data?.rows)) return data.rows;
        if (Array.isArray(data)) return data;
        if (data?.data && typeof data.data === 'object') {
            return Object.values(data.data).filter(v => typeof v === 'object' && v !== null);
        }
        return [];
    };

    // Normalizer for type matching
    const normalizeType = (t) => (t || '').toLowerCase().replace(/[- ]/g, '_').trim();

    // Fetch listing using POST: /content-page-get-by-type and POST: /content-page-list
    const fetchContentPages = async () => {
        setLoading(true);
        try {
            const token = getAuthToken();
            const fetchedPages = [];

            // 1. Fetch each policy using POST: /content-page-get-by-type
            const typePromises = PREDEFINED_POLICIES.map(async (policyDef) => {
                try {
                    const res = await Apiconnection.post(
                        'content-page-get-by-type',
                        { type: policyDef.type },
                        { headers: { token } }
                    );
                    const itemData = res?.data?.data || res?.data;
                    if (itemData && typeof itemData === 'object' && (itemData.id || itemData.type || itemData.title)) {
                        return itemData;
                    }
                } catch (err) {
                    console.warn(`Error fetching ${policyDef.type} by type:`, err);
                }
                return null;
            });

            const byTypeResults = await Promise.all(typePromises);
            byTypeResults.forEach((item) => {
                if (item && (item.id || item.type || item.title)) {
                    fetchedPages.push(item);
                }
            });

            // 2. Also fetch /content-page-list to complement and find any other content pages
            try {
                const listRes = await Apiconnection.post('content-page-list', {}, {
                    headers: { token },
                });
                const fullList = extractListFromResponse(listRes);
                fullList.forEach((listItem) => {
                    const alreadyExists = fetchedPages.some(
                        (p) =>
                            (p.id && listItem.id && p.id === listItem.id) ||
                            (p.type && listItem.type && normalizeType(p.type) === normalizeType(listItem.type))
                    );
                    if (!alreadyExists) {
                        fetchedPages.push(listItem);
                    }
                });
            } catch (listErr) {
                console.warn('content-page-list fetch info:', listErr);
            }

            setContentPages(fetchedPages);
        } catch (error) {
            console.error('Error in fetchContentPages:', error);
        } finally {
            setLoading(false);
        }
    };

    // Backward-compatibility: fetch legacy policies via get-policies
    const fetchLegacyPolicies = async () => {
        try {
            const token = getAuthToken();
            const response = await Apiconnection.post('get-policies', {}, {
                headers: { token },
            });
            if (response?.data?.status) {
                const fetched = response?.data?.data?.policies || '';
                setLegacyPolicy(fetched);
                setLegacyEditorHtml(fetched);
            }
        } catch (error) {
            console.error('Error fetching legacy policies:', error);
        }
    };

    useEffect(() => {
        fetchContentPages();
        fetchLegacyPolicies();
    }, []);

    // Map the 3 predefined policy types and any extra content pages from backend
    const getTableRows = () => {
        const rows = PREDEFINED_POLICIES.map((predef, index) => {
            const found = contentPages.find(
                (item) =>
                    normalizeType(item.type) === normalizeType(predef.type) ||
                    (item.title && item.title.trim().toLowerCase() === predef.title.toLowerCase())
            );

            if (found) {
                return {
                    id: found.id || found._id || null,
                    rowKey: `predef-${predef.type}`,
                    index: index + 1,
                    type: found.type || predef.type,
                    title: found.title || predef.title,
                    description: found.description || found.content || '',
                    updatedAt: found.updatedAt || found.createdAt || null,
                    badgeClass: predef.badgeClass,
                    isConfigured: true,
                    originalItem: found,
                };
            }

            return {
                id: null,
                rowKey: `predef-${predef.type}`,
                index: index + 1,
                type: predef.type,
                title: predef.title,
                description: predef.defaultDescription,
                updatedAt: null,
                badgeClass: predef.badgeClass,
                isConfigured: false,
                originalItem: null,
            };
        });

        // Add any other content pages returned by backend that are not in the predefined list
        contentPages.forEach((item, extraIdx) => {
            const matchesPredef = PREDEFINED_POLICIES.some(
                (predef) =>
                    normalizeType(predef.type) === normalizeType(item.type) ||
                    (item.title && item.title.trim().toLowerCase() === predef.title.toLowerCase())
            );

            if (!matchesPredef) {
                rows.push({
                    id: item.id || item._id || null,
                    rowKey: `extra-${item.id || extraIdx}`,
                    index: rows.length + 1,
                    type: item.type || 'other',
                    title: item.title || 'Untitled Policy',
                    description: item.description || item.content || '',
                    updatedAt: item.updatedAt || item.createdAt || null,
                    badgeClass: 'policy-badge-custom',
                    isConfigured: true,
                    originalItem: item,
                });
            }
        });

        return rows;
    };

    // Open View Modal
    const handleView = (row) => {
        setViewingPolicy(row);
        setOpenViewModal(true);
    };

    // Open Create Modal
    const handleOpenCreateModal = (targetPolicy = null) => {
        setModalMode('create');
        if (targetPolicy) {
            setFormData({
                id: null,
                type: targetPolicy.type,
                title: targetPolicy.title,
                description: targetPolicy.description || targetPolicy.defaultDescription || '',
            });
        } else {
            setFormData({
                id: null,
                type: 'refund_policy',
                title: 'Refund Policy',
                description: '<h1>Refund Policy</h1><p>All sales are final...</p>',
            });
        }
        setOpenEditModal(true);
    };

    // Open Edit Modal for a row
    const handleOpenEditModal = (row) => {
        setModalMode('edit');
        setFormData({
            id: row.id || row.originalItem?.id || row.originalItem?._id || null,
            type: row.type || 'refund_policy',
            title: row.title || '',
            description: row.description || '',
        });
        setOpenEditModal(true);
    };

    // Open Delete Modal
    const handleOpenDeleteModal = (row) => {
        setPolicyToDelete(row);
        setOpenDeleteModal(true);
    };

    // Handle Type change inside modal
    const handleTypeChange = (e) => {
        const newType = e.target.value;
        const matchingPredef = PREDEFINED_POLICIES.find((p) => p.type === newType);
        setFormData((prev) => ({
            ...prev,
            type: newType,
            title: matchingPredef ? matchingPredef.title : prev.title,
            description: prev.description ? prev.description : (matchingPredef ? matchingPredef.defaultDescription : ''),
        }));
    };

    // Save policy: uses POST /content-page-update for edit, POST /content-page-create for add
    const handleSavePolicy = async (e) => {
        if (e) e.preventDefault();

        if (!formData.title?.trim()) {
            toast.error('Policy title is required');
            return;
        }

        if (!formData.description || formData.description === '<p><br></p>') {
            toast.error('Policy description is required');
            return;
        }

        setSaving(true);
        try {
            const token = getAuthToken();
            let response;

            if (modalMode === 'edit' && formData.id) {
                // Edit: POST /content-page-update with { id, title, description }
                const updatePayload = {
                    id: formData.id,
                    title: formData.title.trim(),
                    description: formData.description,
                };
                if (formData.type) {
                    updatePayload.type = formData.type;
                }
                response = await Apiconnection.post('content-page-update', updatePayload, {
                    headers: { token },
                });
            } else {
                // Create: POST /content-page-create with { type, title, description }
                const createPayload = {
                    type: formData.type,
                    title: formData.title.trim(),
                    description: formData.description,
                };
                response = await Apiconnection.post('content-page-create', createPayload, {
                    headers: { token },
                });
            }

            if (response?.data?.status !== false) {
                toast.success(
                    modalMode === 'edit'
                        ? 'Policy updated successfully!'
                        : 'Policy saved successfully!'
                );
                setOpenEditModal(false);
                await fetchContentPages();
            } else {
                toast.error(response?.data?.message || 'Failed to save policy');
            }
        } catch (error) {
            console.error('Error saving content page:', error);
            toast.error(
                error?.response?.data?.message ||
                'An error occurred while saving the policy'
            );
        } finally {
            setSaving(false);
        }
    };

    // Delete policy: POST /content-page-delete with { id }
    const handleDeletePolicy = async () => {
        if (!policyToDelete?.id) {
            toast.error('Cannot delete: Policy ID is missing');
            return;
        }

        setDeleting(true);
        try {
            const token = getAuthToken();
            const deletePayload = {
                id: policyToDelete.id,
            };
            const response = await Apiconnection.post('content-page-delete', deletePayload, {
                headers: { token },
            });

            if (response?.data?.status !== false) {
                toast.success('Policy deleted successfully!');
                setOpenDeleteModal(false);
                setPolicyToDelete(null);
                await fetchContentPages();
            } else {
                toast.error(response?.data?.message || 'Failed to delete policy');
            }
        } catch (error) {
            console.error('Error deleting policy:', error);
            toast.error(
                error?.response?.data?.message ||
                'An error occurred while deleting the policy'
            );
        } finally {
            setDeleting(false);
        }
    };

    // Backward-compatibility: Save legacy policy via policies-add-update
    const handleLegacySaveClick = async () => {
        setLoading(true);
        try {
            const token = getAuthToken();
            const response = await Apiconnection.post(
                'policies-add-update',
                { content: legacyEditorHtml },
                { headers: { token } }
            );
            if (response?.data?.status) {
                setLegacyPolicy(legacyEditorHtml);
                setIsLegacyEditorOpen(false);
                toast.success('Legacy policy updated successfully!');
            } else {
                toast.error(response?.data?.message || 'Failed to save policy');
            }
        } catch (error) {
            console.error('Error saving legacy policies:', error);
            toast.error('Error saving policy');
        } finally {
            setLoading(false);
        }
    };

    const tableRows = getTableRows();

    return (
        <div className='grid-container'>
            <ToastContainer position="top-right" autoClose={3000} />
            <Header OpenSidebar={OpenSidebar} />
            {loading && <Loader />}
            <Sidebar openSidebarToggle={openSidebarToggle} OpenSidebar={OpenSidebar} />

            <div className='inner-container'>
                <div className="policy-sec">
                    {/* Top Section with Title and Action Buttons */}
                    <div className="top-section">
                        <div>
                            <h3>Policies Management</h3>
                            <p style={{ margin: 0, fontSize: '13px', color: '#708399' }}>
                                Manage your Refund Policy, Privacy Policy, and Terms &amp; Conditions
                            </p>
                        </div>
                        <div className='action-btn-sec'>
                            <Button
                                className="refresh-btn"
                                onClick={fetchContentPages}
                                title="Refresh Listing"
                                disabled={loading}
                            >
                                <i className={`fa-solid fa-rotate-right me-1 ${loading ? 'fa-spin' : ''}`}></i>
                                Refresh
                            </Button>
                            <Button
                                variant="contained"
                                className="action-btn"
                                onClick={() => handleOpenCreateModal()}
                            >
                                <AddIcon sx={{ fontSize: '18px', marginRight: '6px' }} /> Add New Policy
                            </Button>
                        </div>
                    </div>

                    {/* Navigation Tabs - General Legacy Policy hidden per user request */}
                    <div className="policy-tabs">
                        <Button
                            className="policy-tab-btn active"
                        >
                            <i className="fa-solid fa-list-check me-2"></i> Policies List (3 Options)
                        </Button>
                    </div>

                    {/* Tab 1: Policies Listing Table */}
                    {activeTab === 'listing' && (
                        <TableContainer
                            component={Paper}
                            className="listing-table-container"
                            style={{ marginTop: 10, background: '#ffffff' }}
                        >
                            <Table className="booking-data-table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ width: '60px' }}>#</TableCell>
                                        <TableCell>Policy Title</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Last Updated</TableCell>
                                        <TableCell align="center" style={{ width: '180px' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableRows.map((row) => (
                                        <TableRow key={row.rowKey}>
                                            <TableCell>{row.index}</TableCell>
                                            <TableCell>
                                                <div className="user-primary-text" style={{ fontWeight: 600, color: '#18324d' }}>
                                                    {row.title}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className={`policy-badge ${row.badgeClass}`}>
                                                    {row.type}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {row.isConfigured ? (
                                                    <span className="policy-status-badge policy-status-active">
                                                        <i className="fa-solid fa-circle-check"></i> Configured
                                                    </span>
                                                ) : (
                                                    <span className="policy-status-badge policy-status-missing">
                                                        <i className="fa-solid fa-circle-exclamation"></i> Not Configured
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {row.updatedAt
                                                    ? moment(row.updatedAt).format('DD MMM YYYY, hh:mm A')
                                                    : '—'}
                                            </TableCell>
                                            <TableCell align="center">
                                                <div className="flx-box user-action-group justify-content-center">
                                                    {row.isConfigured ? (
                                                        <>
                                                            <button
                                                                className="btn btn-sm table-icon-btn table-view-btn me-2"
                                                                title="View Policy"
                                                                onClick={() => handleView(row)}
                                                            >
                                                                <i className="fa-solid fa-eye"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm table-icon-btn table-edit-btn me-2"
                                                                title="Edit Policy"
                                                                onClick={() => handleOpenEditModal(row)}
                                                            >
                                                                <i className="fa-solid fa-pen-to-square"></i>
                                                            </button>
                                                            {row.id && (
                                                                <button
                                                                    className="btn btn-sm table-icon-btn table-delete-btn"
                                                                    title="Delete Policy"
                                                                    onClick={() => handleOpenDeleteModal(row)}
                                                                >
                                                                    <i className="fa-solid fa-trash"></i>
                                                                </button>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            style={{
                                                                borderRadius: '8px',
                                                                fontSize: '12px',
                                                                fontWeight: 600,
                                                                padding: '4px 10px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                            }}
                                                            onClick={() => handleOpenCreateModal(row)}
                                                        >
                                                            <i className="fa-solid fa-plus"></i> Configure
                                                        </button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Tab 2: Legacy Policy Editor (Existing functionality preserved) */}
                    {activeTab === 'legacy' && (
                        <div style={{ marginTop: 20 }}>
                            {isLegacyEditorOpen ? (
                                <ReactQuill
                                    theme="snow"
                                    value={legacyEditorHtml}
                                    onChange={setLegacyEditorHtml}
                                    modules={Policies.modules}
                                    formats={Policies.formats}
                                />
                            ) : (
                                <div
                                    style={{
                                        background: '#ffffff',
                                        padding: '24px',
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                    }}
                                    dangerouslySetInnerHTML={{ __html: legacyPolicy || '<p>No legacy policy configured yet.</p>' }}
                                ></div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal 1: Create / Edit Policy */}
            <Dialog
                open={openEditModal}
                onClose={() => !saving && setOpenEditModal(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: '18px', color: '#18324d' }}>
                        {modalMode === 'edit' ? `Edit Policy: ${formData.title}` : 'Add / Configure Policy'}
                    </div>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenEditModal(false)}
                        disabled={saving}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <form onSubmit={handleSavePolicy}>
                    <DialogContent dividers sx={{ p: 3 }}>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <FormControl fullWidth size="small">
                                    <InputLabel id="policy-type-select-label">Policy Type *</InputLabel>
                                    <Select
                                        labelId="policy-type-select-label"
                                        id="policy-type-select"
                                        value={formData.type}
                                        label="Policy Type *"
                                        onChange={handleTypeChange}
                                        disabled={saving || (modalMode === 'edit' && !!formData.id)}
                                    >
                                        <MenuItem value="refund_policy">Refund Policy (refund_policy)</MenuItem>
                                        <MenuItem value="privacy_policy">Privacy Policy (privacy_policy)</MenuItem>
                                        <MenuItem value="terms_and_conditions">Terms and Conditions (terms_and_conditions)</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>

                            <div className="col-md-6 mb-3">
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Policy Title *"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    required
                                    disabled={saving}
                                />
                            </div>
                        </div>

                        <div className="mb-2">
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#18324d',
                                    marginBottom: '8px',
                                }}
                            >
                                Policy Content / Description (HTML) *
                            </label>
                            <ReactQuill
                                theme="snow"
                                value={formData.description}
                                onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
                                modules={Policies.modules}
                                formats={Policies.formats}
                                placeholder="Enter policy details, clauses, terms, etc..."
                                style={{ minHeight: '220px', marginBottom: '50px' }}
                            />
                        </div>
                    </DialogContent>

                    <DialogActions sx={{ p: 2 }}>
                        <Button
                            onClick={() => setOpenEditModal(false)}
                            disabled={saving}
                            sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={saving}
                            className="action-btn"
                            style={{ minWidth: '120px' }}
                        >
                            {saving ? 'Saving...' : 'Save Policy'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Modal 2: View Policy Preview */}
            <Dialog
                open={openViewModal}
                onClose={() => setOpenViewModal(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontWeight: 700, fontSize: '18px', color: '#18324d' }}>
                            {viewingPolicy?.title}
                        </span>
                        {viewingPolicy?.type && (
                            <span className={`policy-badge ${viewingPolicy.badgeClass || 'policy-badge-custom'}`}>
                                {viewingPolicy.type}
                            </span>
                        )}
                    </div>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenViewModal(false)}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 3 }}>
                    <div className="policy-preview-modal-body">
                        {viewingPolicy?.description ? (
                            <div dangerouslySetInnerHTML={{ __html: viewingPolicy.description }} />
                        ) : (
                            <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>No content available for this policy.</p>
                        )}
                    </div>
                    {viewingPolicy?.updatedAt && (
                        <div style={{ marginTop: '12px', fontSize: '12px', color: '#64748b', textAlign: 'right' }}>
                            Last updated: {moment(viewingPolicy.updatedAt).format('DD MMM YYYY, hh:mm A')}
                        </div>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setOpenViewModal(false)}
                        sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        className="action-btn"
                        onClick={() => {
                            setOpenViewModal(false);
                            if (viewingPolicy) {
                                handleOpenEditModal(viewingPolicy);
                            }
                        }}
                    >
                        <i className="fa-solid fa-pen-to-square me-1"></i> Edit Policy
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal 3: Delete Confirmation */}
            <Dialog
                open={openDeleteModal}
                onClose={() => !deleting && setOpenDeleteModal(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#dc2626' }}>
                    <i className="fa-solid fa-triangle-exclamation me-2"></i> Delete Policy
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#334155' }}>
                        Are you sure you want to delete <strong>{policyToDelete?.title}</strong>?
                        This action will remove the policy configuration.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setOpenDeleteModal(false)}
                        disabled={deleting}
                        sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeletePolicy}
                        disabled={deleting}
                        sx={{
                            backgroundColor: '#dc2626 !important',
                            color: '#ffffff !important',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '10px !important',
                        }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

Policies.modules = {
    toolbar: [
        [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['bold', 'italic', 'underline'],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
    ],
};

Policies.formats = [
    'header', 'font', 'list', 'bullet',
    'bold', 'italic', 'underline', 'align',
    'link', 'image'
];

export default Policies;
